import { CrmTaskStatus } from '@plato/data/prisma';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TaskCompletedSource } from '../../analytics/types/analytics-events.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';

export type UseToggleTaskHook = {
    isPending: boolean;
    pendingTasks: string[];
    setTaskComplete: (taskId: string, isComplete: boolean) => void;
};

export function useToggleTask(source: TaskCompletedSource): UseToggleTaskHook {
    const { t } = useTranslation('tasks');
    const { toast } = useToast();
    const { openTaskDetails } = useDialogs();
    const [pendingTasks, setPendingTasks] = useState<string[]>([]);

    const sendEvent = useAnalytics();
    const apiUtils = api.useUtils();

    const { isPending, mutate } = api.platoUser.command.tasks.postTasksActions.statusChange.useMutation({
        onSettled: (data, error, input) => {
            setPendingTasks((prev) => prev.filter((x) => x !== input.taskId));
        },
        onSuccess(task, variables) {
            void Promise.all([
                apiUtils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate(),
                apiUtils.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.invalidate(),
                apiUtils.platoUser.query.tasks.getManyTasks.find.reset({ taskId: task.taskId }),
                apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate(),
                apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate(),
                apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate(),
                apiUtils.platoUser.query.notes.getFollowUps.invalidate(),
            ]);

            // task is the mutation response, so task.status already equals variables.status —
            // comparing them can never detect a transition. The checkbox only sends COMPLETED
            // for open tasks, so the intent alone is the right trigger.
            if (variables.status === 'COMPLETED') {
                sendEvent('task_completed', {
                    task_id: task.taskId,
                    due_date: task.dueDate.toISOString(),
                    assignee_id: task.assignedTo.id,
                    assignor_id: task.assignedBy.id,
                    is_assigned_to_oneself: task.assignedTo.id === task.assignedBy.id,
                    related_entity_type: task.links[0]?.modelType,
                    related_entity_id: task.links[0]?.modelId,
                    source,
                });
            }

            toast({
                icon: 'success',
                title: t('tasks__toast__edited_success_title'),
                description: (
                    <Trans
                        i18nKey={'tasks__toast__success_body'}
                        ns="tasks"
                        components={{
                            linkTag: (
                                <Link
                                    className="text-blue-700 hover:underline"
                                    to={`/tasks/${encodeURIComponent(task.taskId)}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openTaskDetails(task.taskId);
                                        return null;
                                    }}
                                />
                            ),
                        }}
                    />
                ),
            });
        },
        onError(error) {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const setTaskComplete = useCallback(
        (taskId: string, checked: boolean) => {
            setPendingTasks((prev) => [...prev, taskId]);
            mutate({
                taskId,
                status: checked ? CrmTaskStatus.COMPLETED : CrmTaskStatus.OPEN,
            });
        },
        [mutate],
    );

    return {
        isPending,
        pendingTasks,
        setTaskComplete,
    };
}
